import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';
import { invoiceService } from '../../services/invoice.service';

const Analytics = ({ onBack }) => {
  const [analyticsData, setAnalyticsData] = useState({
    llmConfidence: 0,
    validationStageRate: 0,
    unmatchDetectionRate: 0,
    payeeQualityIndex: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch documents from raw_invoices basket where all metadata is kept
      const documentsResponse = await invoiceService.getDocuments('raw_invoices');
      const documents = documentsResponse.documents || [];


      // Calculate analytics metrics
      const analytics = calculateAnalytics(documents, []);
      setAnalyticsData(analytics);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch analytics data:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to extract value from the complex metadata structure
  const extractMetadataValue = (metadataField) => {
    if (!metadataField) return null;
    
    try {
      // If it's already a simple value, return it
      if (typeof metadataField === 'number' || typeof metadataField === 'boolean') {
        return metadataField;
      }
      
      // If it's an object with extra.value structure, extract the value
      if (typeof metadataField === 'object' && metadataField.extra && metadataField.extra.value !== undefined) {
        return metadataField.extra.value;
      }
      
      // If it's a JSON string, try to parse it
      if (typeof metadataField === 'string') {
        // First try to parse as JSON
        const parsed = JSON.parse(metadataField);
        
        // If the parsed object has extra.value structure, extract the value
        if (parsed && typeof parsed === 'object' && parsed.extra && parsed.extra.value !== undefined) {
          return parsed.extra.value;
        }
        
        // If it's just a simple string value, return it
        return parsed;
      }
      
      return metadataField;
    } catch (error) {
      console.warn('Failed to extract metadata value:', metadataField, error);
      return null;
    }
  };

  const calculateAnalytics = (documents, baskets) => {
    
    // 1. Average LLM Confidence Score
    let totalConfidence = 0;
    let confidenceCount = 0;
    
    // 2. Validation Stage Rate (raw_invoices -> ready_to_pay + need_validation)
    let rawDocuments = 0;
    let validationStageDocuments = 0;
    
    // 3. Unmatch Detection Rate (documents that failed validation)
    let totalProcessed = 0;
    let unmatchDetected = 0;
    
    // 4. Payee Quality Index
    const payeeStats = {};

    // Additional metrics for better insights
    let totalDocuments = documents.length;
    let processedDocuments = 0;
    let confidenceScores = [];

    documents.forEach((doc, index) => {
      const processingMetadata = doc.processing_metadata || {};
      
      // Extract values using the helper function
      const processingStatus = extractMetadataValue(processingMetadata.processing_status);
      const extractionConfidence = extractMetadataValue(processingMetadata.extraction_confidence);
      const poValidationStatus = extractMetadataValue(processingMetadata.po_validation_status);
      const supplierId = extractMetadataValue(processingMetadata.supplier_id);
      const supplierName = extractMetadataValue(processingMetadata.supplier_name);
      

      // LLM Confidence Score
      if (extractionConfidence && typeof extractionConfidence === 'number' && extractionConfidence > 0) {
        totalConfidence += extractionConfidence;
        confidenceCount++;
        confidenceScores.push(extractionConfidence);
      }

      // Count processed documents
      if (processingStatus === 'completed' || processingStatus === 'success') {
        processedDocuments++;
      }

      // Validation Stage Rate - documents that went from raw to validation stage
      if (doc.basket_name === 'raw_invoices') {
        rawDocuments++;
        
        // Count documents that have progressed to validation stage (valid or invalid)
        if (poValidationStatus === 'valid' || poValidationStatus === 'invalid') {
          validationStageDocuments++;
        }
      }

      // Unmatch Detection Rate - documents that failed validation
      if (processingStatus === 'completed' || processingStatus === 'success') {
        totalProcessed++;
        
        // Check for validation failures
        if (poValidationStatus === 'invalid') {
          unmatchDetected++;
        }
      }

      // Payee Quality Index - aggregate by supplier
      const payeeId = supplierId || 'Unknown';
      const payeeName = supplierName || payeeId;

      if (!payeeStats[payeeId]) {
        payeeStats[payeeId] = {
          name: payeeName,
          totalInvoices: 0,
          readyToPay: 0,
          furtherValidation: 0,
          invalid: 0
        };
      }
      
      payeeStats[payeeId].totalInvoices++;
      
      // Categorize by validation status
      if (poValidationStatus === 'valid') {
        payeeStats[payeeId].readyToPay++;
      } else if (poValidationStatus === 'invalid') {
        payeeStats[payeeId].invalid++;
      } else {
        payeeStats[payeeId].furtherValidation++;
      }
    });

    // Calculate final metrics
    const averageConfidence = confidenceCount > 0 ? (totalConfidence / confidenceCount) * 100 : 0;
    const validationStageRate = rawDocuments > 0 ? (validationStageDocuments / rawDocuments) * 100 : 0;
    const unmatchDetectionRate = totalProcessed > 0 ? (unmatchDetected / totalProcessed) * 100 : 0;

    // Calculate Payee Quality Index with more detailed metrics
    const payeeQualityIndex = Object.entries(payeeStats)
      .filter(([_, stats]) => stats.totalInvoices >= 1) // Include all payees with at least 1 invoice
      .map(([payeeId, stats]) => ({
        payeeId,
        name: stats.name,
        totalInvoices: stats.totalInvoices,
        readyToPay: stats.readyToPay,
        furtherValidation: stats.furtherValidation,
        invalid: stats.invalid,
        passingRatio: stats.totalInvoices > 0 ? (stats.readyToPay / stats.totalInvoices) * 100 : 0,
        reviewRatio: stats.totalInvoices > 0 ? (stats.furtherValidation / stats.totalInvoices) * 100 : 0,
        failureRatio: stats.totalInvoices > 0 ? (stats.invalid / stats.totalInvoices) * 100 : 0
      }))
      .sort((a, b) => b.passingRatio - a.passingRatio)
      .slice(0, 10); // Top 10 payees


    return {
      llmConfidence: averageConfidence,
      validationStageRate,
      unmatchDetectionRate,
      payeeQualityIndex,
      // Additional insights
      totalDocuments,
      processedDocuments,
      confidenceScores: confidenceScores.sort((a, b) => b - a), // Sorted descending
      rawDocuments,
      validationStageDocuments,
      totalProcessed,
      unmatchDetected
    };
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  // Chart configurations
  const confidenceChartOptions = {
    chart: {
      type: 'radialBar',
      height: 300
    },
    plotOptions: {
      radialBar: {
        startAngle: -90,
        endAngle: 90,
        dataLabels: {
          name: {
            fontSize: '16px',
            color: undefined,
            offsetY: -10
          },
          value: {
            fontSize: '24px',
            color: undefined,
            offsetY: 16,
            formatter: function (val) {
              return val.toFixed(1) + '%'
            }
          }
        }
      }
    },
    colors: ['#00E396'],
    labels: ['LLM Confidence Score'],
    title: {
      text: 'Average LLM Confidence Score',
      align: 'center',
      style: {
        fontSize: '16px',
        fontWeight: 'bold'
      }
    }
  };

  const validationChartOptions = {
    chart: {
      type: 'donut',
      height: 300
    },
    labels: ['Validation Stage', 'Still Processing'],
    colors: ['#00E396', '#FFB347'],
    title: {
      text: 'Raw Documents to Validation Stage',
      align: 'center',
      style: {
        fontSize: '16px',
        fontWeight: 'bold'
      }
    },
    legend: {
      position: 'bottom'
    }
  };

  const unmatchChartOptions = {
    chart: {
      type: 'donut',
      height: 300
    },
    labels: ['Valid Matches', 'Unmatch Detected'],
    colors: ['#00E396', '#FF6B6B'],
    title: {
      text: 'Unmatch Detection Rate',
      align: 'center',
      style: {
        fontSize: '16px',
        fontWeight: 'bold'
      }
    },
    legend: {
      position: 'bottom'
    }
  };

  const payeeChartOptions = {
    chart: {
      type: 'bar',
      height: 400
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4
      }
    },
    dataLabels: {
      enabled: false
    },
    colors: ['#008FFB'],
    xaxis: {
      categories: analyticsData.payeeQualityIndex.map(payee => payee.name),
      title: {
        text: 'Passing Ratio (%)'
      },
      min: 0,
      max: 100
    },
    yaxis: {
      title: {
        text: 'Payees'
      }
    },
    title: {
      text: 'Payee Quality Index (Top 10)',
      align: 'center',
      style: {
        fontSize: '16px',
        fontWeight: 'bold'
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Tooltip title="Back to Dashboard">
              <IconButton onClick={onBack} color="primary">
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="h4" component="h1" gutterBottom>
              Invoice Processing Analytics
            </Typography>
          </Box>
          <Tooltip title="Refresh Analytics">
            <IconButton onClick={fetchAnalyticsData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={fetchAnalyticsData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={onBack} color="primary">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Invoice Processing Analytics
            </Typography>
            {lastUpdated && (
              <Typography variant="caption" color="text.secondary">
                Last updated: {lastUpdated.toLocaleString()}
              </Typography>
            )}
          </Box>
        </Box>
        <Tooltip title="Refresh Analytics">
          <IconButton onClick={fetchAnalyticsData} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    LLM Confidence
                  </Typography>
                  <Typography variant="h4" component="div">
                    {analyticsData.llmConfidence.toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {analyticsData.confidenceScores?.length || 0} documents analyzed
                  </Typography>
                </Box>
                <AssessmentIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Validation Stage
                  </Typography>
                  <Typography variant="h4" component="div">
                    {analyticsData.validationStageRate.toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {analyticsData.validationStageDocuments || 0} of {analyticsData.rawDocuments || 0} raw docs
                  </Typography>
                </Box>
                <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Unmatch Detected
                  </Typography>
                  <Typography variant="h4" component="div">
                    {analyticsData.unmatchDetectionRate.toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {analyticsData.unmatchDetected || 0} of {analyticsData.totalProcessed || 0} processed
                  </Typography>
                </Box>
                <WarningIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Top Payee Quality
                  </Typography>
                  <Typography variant="h4" component="div">
                    {analyticsData.payeeQualityIndex.length > 0 ? 
                      analyticsData.payeeQualityIndex[0].passingRatio.toFixed(1) + '%' : 
                      'N/A'
                    }
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {analyticsData.payeeQualityIndex.length} payees analyzed
                  </Typography>
                </Box>
                <BusinessIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>


      {/* Additional Insights */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Processing Overview
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Total Documents:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {analyticsData.totalDocuments || 0}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Processed:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {analyticsData.processedDocuments || 0}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Processing Rate:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {analyticsData.totalDocuments > 0 ? 
                    ((analyticsData.processedDocuments / analyticsData.totalDocuments) * 100).toFixed(1) + '%' : 
                    '0%'
                  }
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Confidence Distribution
              </Typography>
              {analyticsData.confidenceScores && analyticsData.confidenceScores.length > 0 ? (
                <>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Highest:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {(analyticsData.confidenceScores[0] * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Lowest:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {(analyticsData.confidenceScores[analyticsData.confidenceScores.length - 1] * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Median:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {(analyticsData.confidenceScores[Math.floor(analyticsData.confidenceScores.length / 2)] * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No confidence data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Validation Summary
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Raw Documents:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {analyticsData.rawDocuments || 0}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Auto Validated:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'success.main' }}>
                  {analyticsData.autoValidated || 0}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Needs Review:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'warning.main' }}>
                  {(analyticsData.rawDocuments || 0) - (analyticsData.autoValidated || 0)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* LLM Confidence Chart */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={confidenceChartOptions}
                series={[analyticsData.llmConfidence]}
                type="radialBar"
                height={300}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Auto Validation Chart */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={validationChartOptions}
                series={[analyticsData.validationStageRate, 100 - analyticsData.validationStageRate]}
                type="donut"
                height={300}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Unmatch Detection Chart */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={unmatchChartOptions}
                series={[100 - analyticsData.unmatchDetectionRate, analyticsData.unmatchDetectionRate]}
                type="donut"
                height={300}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Payee Quality Index Chart and Table */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <ReactApexChart
                options={payeeChartOptions}
                series={[{
                  name: 'Passing Ratio',
                  data: analyticsData.payeeQualityIndex.map(payee => payee.passingRatio)
                }]}
                type="bar"
                height={400}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payee Quality Details
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Payee</TableCell>
                      <TableCell align="right">Passing Ratio</TableCell>
                      <TableCell align="right">Ready to Pay</TableCell>
                      <TableCell align="right">Needs Review</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analyticsData.payeeQualityIndex.map((payee, index) => (
                      <TableRow key={payee.payeeId}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {payee.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {payee.payeeId}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${payee.passingRatio.toFixed(1)}%`}
                            color={payee.passingRatio >= 80 ? 'success' : 
                                   payee.passingRatio >= 60 ? 'warning' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'medium' }}>
                            {payee.readyToPay}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 'medium' }}>
                            {payee.furtherValidation}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {payee.totalInvoices}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Analytics;
